// src/components/customers/CustomerCard.tsx
import React from 'react';
import { MoreHorizontal, CheckCircle2, AlertCircle, Mail, Lock, Smartphone, UserPlus } from 'lucide-react';
import { Customer } from '../modals/CustomerDetailModal';
import { useTranslation } from 'react-i18next';
import { useClients } from '@/contexts/ClientsContext';

interface CustomerCardProps {
    customer: Customer;
    onClick: () => void;
}

const CustomerCard: React.FC<CustomerCardProps> = ({ customer, onClick }) => {
    const { t } = useTranslation();
    const { blackList } = useClients();

    // Check if blocked either by property or blacklist context
    const isBlocked = customer.isBlocked || (blackList && blackList.includes(customer.id));

    return (
        <div
            onClick={onClick}
            className={`group bg-white rounded-3xl p-6 border shadow-sm hover:shadow-xl hover:shadow-gray-200/50 transition-all cursor-pointer relative overflow-hidden ${customer.merged_into_id ? 'opacity-60 grayscale-[0.5] border-gray-200' : isBlocked
                ? 'border-red-200 bg-red-50/10' // Slight red tint if blocked
                : 'border-gray-100 hover:border-clip-100'
                }`}
        >
            {/* Hover Top Accent - Red if blocked, Clip Color otherwise */}
            <div
                className={`absolute top-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300 ${customer.merged_into_id ? 'bg-gray-400' : isBlocked ? 'bg-red-500' : 'bg-clip-500'
                    }`}
            />

            <div className="flex items-start justify-between mb-6">
                <div className="relative">
                    <div
                        className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold uppercase ${customer.avatarColor} group-hover:scale-110 transition-transform duration-300`}
                    >
                        {customer.firstName?.[0]}
                        {customer.lastName?.[0]}
                    </div>

                    {/* Blocked Badge on Avatar */}
                    {isBlocked && (
                        <div className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full shadow-md border-2 border-white z-10" title={t('customers.modal.block')}>
                            <Lock size={12} />
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    className="p-2 text-gray-300 hover:text-clip-500 hover:bg-clip-50 rounded-full transition-colors"
                >
                    <MoreHorizontal size={20} />
                </button>
            </div>

            <div className="mb-4">
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-clip-600 transition-colors">
                    {customer.firstName} {customer.lastName}
                </h3>

                <div className="flex flex-wrap items-center gap-2 mt-1">
                    {/* Status Badge */}
                    {isBlocked ? (
                        <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-red-100 text-red-700 border-red-200">
                            <Lock size={10} />
                            {t('customers.modal.blocked')}
                        </span>
                    ) : (
                        <span
                            className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${customer.isEmailVerified
                                ? 'bg-green-50 text-green-700 border-green-100'
                                : 'bg-orange-50 text-orange-700 border-orange-100' // Changed red to orange for unverified to distinguish from blocked
                                }`}
                        >
                            {customer.isEmailVerified ? <CheckCircle2 size={10} /> : <AlertCircle size={10} />}
                            {customer.isEmailVerified ? t('customers.verified') : t('customers.not_verified')}
                        </span>
                    )}
                    {/* Origine: creato dal salone (manuale) o dall'app cliente */}
                    <span
                        className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${customer.isManuallyEntry ? 'bg-gray-50 text-gray-600 border-gray-200' : 'bg-clip-50 text-clip-600 border-clip-100'}`}
                    >
                        {customer.isManuallyEntry ? <UserPlus size={10} /> : <Smartphone size={10} />}
                        {customer.isManuallyEntry ? t('customers.created_by_you', 'Creato da te') : t('customers.created_by_app', 'Creato da app')}
                    </span>
                    {/* Merged Badge */}
                    {customer.merged_into_id && (
                        <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-gray-100 text-gray-700 border-gray-200">
                            {t('customers.merged_badge')}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-2 mt-3 text-sm text-gray-500 truncate">
                    <Mail size={14} className="flex-shrink-0" />
                    <span className="truncate">{customer.email || 'N/A'}</span>
                </div>
            </div>

            <div className="pt-4 border-t border-gray-50 flex items-center justify-between text-sm">
                <div className="text-center">
                    <p className="text-gray-400 text-xs mb-0.5">{t('customers.appointments')}</p>
                    <p className="font-bold text-gray-900 text-lg">{customer.totalAppointments}</p>
                </div>
                <div className="w-px h-8 bg-gray-100" />
                <div className="text-center">
                    <p className="text-gray-400 text-xs mb-0.5">{t('customers.last_visit')}</p>
                    <p className="font-bold text-gray-900 text-lg tracking-tight">
                        {customer.lastVisit}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CustomerCard;